import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  User,
  Loader2,
  UserPlus,
  Globe,
  Lock,
  UserCheck,
  Trophy,
  TrendingUp,
  Calendar,
  Star,
  Flame,
  Clock,
  Shield,
  Sword,
  ShoppingCart,
  Users,
  Swords
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png"
};

const getBossImage = (bossName) => {
  const baseName = bossName?.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
];

const UserProfileModal = ({ open, onOpenChange, username, userId = null, onClose }) => {
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [loadingStats, setLoadingStats] = useState(false);
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [activeTab, setActiveTab] = useState("stats");

  // Support both open/onOpenChange and legacy onClose pattern
  const isOpen = open !== undefined ? open : true;
  const handleOpenChange = (val) => {
    if (onOpenChange) onOpenChange(val);
    if (!val && onClose) onClose();
  };

  const loadUserProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUserProfile(null);
    setUserStats(null);

    try {
      let targetUserId = userId;
      
      if (!targetUserId && username) {
        const searchResponse = await axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(username)}`);
        const foundUser = searchResponse.data.users?.find(
          u => u.username.toLowerCase() === username.toLowerCase()
        );
        if (foundUser) {
          targetUserId = foundUser.id;
        } else {
          setError("User not found");
          setLoading(false);
          return;
        }
      }

      setResolvedUserId(targetUserId);

      const profileResponse = await axios.get(`${API_URL}/api/users/${targetUserId}/public-profile`);
      setUserProfile(profileResponse.data);

      try {
        const statsResponse = await axios.get(`${API_URL}/api/users/${targetUserId}/stats`);
        setUserStats(statsResponse.data);
      } catch (statsError) {
        if (statsError.response?.status === 403) {
          setUserStats({ private: true });
        }
      }
    } catch (error) {
      setError("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  }, [username, userId]);

  useEffect(() => {
    if (isOpen && (username || userId)) {
      setSelectedPeriod("all");
      setActiveTab("stats");
      loadUserProfile();
    }
  }, [isOpen, username, userId, loadUserProfile]);

  const loadFilteredStats = async (period) => {
    if (!resolvedUserId || userStats?.private) return;
    setLoadingStats(true);
    setSelectedPeriod(period);
    try {
      let url = `${API_URL}/api/users/${resolvedUserId}/stats`;
      if (period !== "all") url += `?period=${period}`;
      const response = await axios.get(url);
      setUserStats(response.data);
    } catch (error) {
      toast.error("Failed to load statistics");
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!userProfile) return;
    try {
      await axios.post(`${API_URL}/api/friends/request`, {
        recipient_id: userProfile.id
      });
      toast.success("Friend request sent!");
      loadUserProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send friend request");
    }
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case "public": return <Globe className="w-3 h-3 text-green-400" />;
      case "friends_only": return <UserCheck className="w-3 h-3 text-blue-400" />;
      case "private": return <Lock className="w-3 h-3 text-red-400" />;
      default: return <Globe className="w-3 h-3 text-green-400" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatMesos = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return `${num.toLocaleString()},000,000`;
  };

  const allBounties = userProfile ? [...(userProfile.active_bounties || []), ...(userProfile.hunting_bounties || [])] : [];
  const allExpeditions = userProfile ? [...(userProfile.active_expeditions || []), ...(userProfile.joined_expeditions || [])] : [];
  // Deduplicate by id
  const uniqueExpeditions = allExpeditions.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="user-profile-modal">
        <DialogHeader>
          <DialogTitle>User Profile</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-muted-foreground">{error}</div>
          ) : userProfile ? (
            <div className="space-y-4">
              {/* User Info Header */}
              <div className="flex items-center gap-4 p-4 rounded-lg bg-card/50 border border-border/50">
                {userProfile.avatar ? (
                  <img src={userProfile.avatar} alt={userProfile.username} className="w-16 h-16 rounded-full object-cover border-4 border-border" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center border-4 border-border">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-bold">{userProfile.username}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {getPrivacyIcon(userProfile.profile_privacy)}
                    <span className="capitalize">{userProfile.profile_privacy?.replace("_", " ") || "Public"}</span>
                  </div>
                  {userProfile.main_guild && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                      {userProfile.main_guild.emblem && (
                        <img src={userProfile.main_guild.emblem} alt="" className="w-4 h-4 rounded object-cover" />
                      )}
                      {userProfile.main_guild.name}
                    </div>
                  )}
                  {/* Reputation */}
                  <div className="flex items-center gap-3 mt-1">
                    {(userProfile.hunter_reputation > 0 || userProfile.client_reputation > 0) && (
                      <>
                        <span className="flex items-center gap-1 text-xs text-blue-400" title="Hunter Reputation">
                          <Swords className="w-3 h-3" /> {userProfile.hunter_reputation || 0} Hunter Rep
                        </span>
                        <span className="flex items-center gap-1 text-xs text-amber-400" title="Client Reputation">
                          <ShoppingCart className="w-3 h-3" /> {userProfile.client_reputation || 0} Client Rep
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Member since {new Date(userProfile.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>

                {user && userProfile.id !== user.id && (
                  <div>
                    {userProfile.is_friend ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm">
                        <UserCheck className="w-4 h-4" /> Friends
                      </div>
                    ) : userProfile.friend_request_status === "pending_sent" ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm">
                        <Clock className="w-4 h-4" /> Pending
                      </div>
                    ) : userProfile.friend_request_status === "pending_received" ? (
                      <Button size="sm" onClick={() => toast.info("Check your Profile > Friends tab to accept")}>
                        <UserPlus className="w-4 h-4 mr-2" /> Accept
                      </Button>
                    ) : (
                      <Button size="sm" onClick={handleSendFriendRequest} data-testid="add-friend-btn">
                        <UserPlus className="w-4 h-4 mr-2" /> Add Friend
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Guilds */}
              {userProfile.guilds && userProfile.guilds.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {userProfile.guilds.map((g) => (
                    <Badge key={g.id} variant="outline" className="gap-1 text-xs">
                      {g.emblem && <img src={g.emblem} alt="" className="w-3 h-3 rounded" />}
                      {g.name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Tab Selector */}
              <div className="flex gap-1 border-b border-border/50 pb-0">
                {[
                  { id: "stats", label: "Boss Stats", icon: Trophy },
                  { id: "hc", label: "Hunter's Chance", icon: Swords },
                  { id: "chars", label: "Characters", icon: Users },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-md transition-colors ${
                      activeTab === tab.id
                        ? "bg-primary/10 text-primary border-b-2 border-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                    data-testid={`profile-tab-${tab.id}`}
                  >
                    <tab.icon className="w-3.5 h-3.5" /> {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab: Boss Stats */}
              {activeTab === "stats" && (
                <>
                  {userStats ? (
                    userStats.private ? (
                      <div className="p-8 rounded-lg bg-muted/50 text-center">
                        <Lock className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                        <p className="text-muted-foreground">This user's statistics are private</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {PERIOD_OPTIONS.map((option) => (
                            <Button key={option.value} size="sm" variant={selectedPeriod === option.value ? "default" : "outline"} onClick={() => loadFilteredStats(option.value)} disabled={loadingStats}>
                              {option.label}
                            </Button>
                          ))}
                        </div>

                        {loadingStats && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin" />
                          </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="p-3 rounded-lg bg-background/50 border border-border/50 text-center">
                            <Trophy className="w-5 h-5 mx-auto mb-1 text-primary" />
                            <p className="text-xl font-bold">{selectedPeriod === "all" ? userStats.total_kills : userStats.filtered_kills}</p>
                            <p className="text-xs text-muted-foreground">{selectedPeriod === "all" ? "Total" : "Period"} Kills</p>
                          </div>
                          <div className="p-3 rounded-lg bg-background/50 border border-border/50 text-center">
                            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
                            <p className="text-xl font-bold">{userStats.weekly_kills}</p>
                            <p className="text-xs text-muted-foreground">This Week</p>
                          </div>
                          <div className="p-3 rounded-lg bg-background/50 border border-border/50 text-center">
                            <Calendar className="w-5 h-5 mx-auto mb-1 text-amber-400" />
                            <p className="text-xl font-bold">{userStats.monthly_kills}</p>
                            <p className="text-xs text-muted-foreground">This Month</p>
                          </div>
                          <div className="p-3 rounded-lg bg-background/50 border border-border/50 text-center">
                            <Star className="w-5 h-5 mx-auto mb-1 text-rose-400" />
                            <p className="text-xl font-bold">{userStats.best_day?.kills || 0}</p>
                            <p className="text-xs text-muted-foreground">Best Day</p>
                          </div>
                        </div>

                        {userStats.best_day && (
                          <div className="p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border border-amber-500/20">
                            <div className="flex items-center gap-3">
                              <Trophy className="w-6 h-6 text-amber-400" />
                              <div>
                                <p className="text-sm font-medium text-amber-400">Personal Best</p>
                                <p className="text-xs text-muted-foreground">
                                  {userStats.best_day.kills} kills on {new Date(userStats.best_day.date).toLocaleDateString("en-US", { weekday: 'short', month: 'short', day: 'numeric' })}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {userStats.kills_by_boss && Object.keys(userStats.kills_by_boss).length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-semibold flex items-center gap-2">
                              <Flame className="w-4 h-4 text-red-400" /> Top Hunted Bosses
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(userStats.kills_by_boss).slice(0, 8).map(([boss, count], idx) => (
                                <div key={boss} className="flex items-center gap-2 p-2 rounded bg-background/50 border border-border/50">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    idx === 0 ? "bg-yellow-500/20 text-yellow-400" : idx === 1 ? "bg-slate-400/20 text-slate-300" : idx === 2 ? "bg-amber-700/20 text-amber-600" : "bg-muted text-muted-foreground"
                                  }`}>{idx + 1}</span>
                                  <img src={getBossImage(boss)} alt={boss} className="w-8 h-8 object-contain" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{boss.split(" - ")[0]}</p>
                                  </div>
                                  <span className="text-sm font-bold text-primary">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {userStats.recent_kills && userStats.recent_kills.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-semibold flex items-center gap-2">
                              <Clock className="w-4 h-4" /> Recent Kills
                            </h4>
                            <div className="space-y-1 max-h-[200px] overflow-y-auto">
                              {userStats.recent_kills.slice(0, 10).map((kill) => (
                                <div key={kill.id} className="flex items-center gap-2 p-2 rounded bg-background/50 border border-border/50">
                                  <img src={getBossImage(kill.boss_name)} alt={kill.boss_name} className="w-8 h-8 object-contain" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{kill.boss_name}</p>
                                    <p className="text-xs text-muted-foreground">CH {kill.channel + 1}</p>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{formatDate(kill.killed_at)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  ) : null}
                </>
              )}

              {/* Tab: Hunter's Chance */}
              {activeTab === "hc" && (
                <div className="space-y-4">
                  {/* Active Expeditions */}
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <Swords className="w-4 h-4 text-purple-400" /> Active Expeditions
                    </h4>
                    {uniqueExpeditions.length > 0 ? (
                      <div className="space-y-1.5">
                        {uniqueExpeditions.map((exp) => (
                          <a key={exp.id} href={`/hunters-chance/expedition/${exp.id}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/20 hover:bg-purple-500/10 transition-colors cursor-pointer" data-testid={`profile-expedition-${exp.id}`}>
                            <img src={getBossImage(exp.boss_name)} alt={exp.boss_name} className="w-8 h-8 object-contain" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{exp.boss_name}</p>
                              {exp.scheduled_time && <p className="text-[10px] text-muted-foreground">{new Date(exp.scheduled_time).toLocaleString()}</p>}
                            </div>
                            <Badge variant="outline" className="text-[10px]">{exp.status}</Badge>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground py-2">No active expeditions</p>
                    )}
                  </div>

                  {/* Active Bounties */}
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <Shield className="w-4 h-4 text-red-400" /> Active Bounties
                    </h4>
                    {allBounties.length > 0 ? (
                      <div className="space-y-1.5">
                        {allBounties.map((b) => (
                          <a key={b.id} href={`/hunters-chance/bounty/${b.id}`} className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 transition-colors cursor-pointer" data-testid={`profile-bounty-${b.id}`}>
                            <img src={getBossImage(b.boss_name)} alt={b.boss_name} className="w-8 h-8 object-contain" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{b.boss_name}</p>
                              {b.reward && <p className="text-[10px] text-amber-400">{formatMesos(b.reward)} mesos</p>}
                            </div>
                            <Badge variant="outline" className="text-[10px]">{b.status}</Badge>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground py-2">No active bounties</p>
                    )}
                  </div>

                  {/* Reputation Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-center">
                      <Swords className="w-5 h-5 mx-auto mb-1 text-blue-400" />
                      <p className="text-xl font-bold text-blue-300">{userProfile.hunter_reputation || 0}</p>
                      <p className="text-xs text-muted-foreground">Hunter Reputation</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-center">
                      <ShoppingCart className="w-5 h-5 mx-auto mb-1 text-amber-400" />
                      <p className="text-xl font-bold text-amber-300">{userProfile.client_reputation || 0}</p>
                      <p className="text-xs text-muted-foreground">Client Reputation</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Characters */}
              {activeTab === "chars" && (
                <div className="space-y-2">
                  {userProfile.characters && userProfile.characters.length > 0 ? (
                    userProfile.characters.map((char) => (
                      <div key={char.id || char.name} className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/50" data-testid={`profile-char-${char.name}`}>
                        <div className="flex gap-1">
                          {(char.skills || []).slice(0, 2).map((skill, si) => (
                            <img key={si} src={skill.icon} alt={skill.name} className="w-7 h-7" title={skill.name} />
                          ))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{char.name || "Unnamed"}</p>
                          <p className="text-xs text-muted-foreground">{char.job} - Lv.{char.level}{char.hp ? ` - HP: ${char.hp.toLocaleString()}` : ""}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px]" style={{ borderColor: char.color || "#666", color: char.color || "#666" }}>
                          {char.job}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground py-4 text-center">No characters registered</p>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal;
